import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

// Admin timestamps. Everything is stored and sent as ISO-8601 UTC; the admin pages show it in the zone the admin
// picked (saved per browser in localStorage, default Australia/Sydney), e.g. "25 Oct 2026, 9:07am AEDT", with the
// UTC value on hover.

export const TZ_STORAGE_KEY = "hs-admin-timezone";
export const DEFAULT_TZ = "Australia/Sydney";
export const TIMEZONES = [
  "Australia/Sydney", "Australia/Melbourne", "Australia/Brisbane", "Australia/Adelaide", "Australia/Darwin",
  "Australia/Hobart", "Australia/Perth", "Pacific/Auckland", "Asia/Singapore", "Asia/Tokyo", "Asia/Kolkata",
  "Europe/London", "Europe/Berlin", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "UTC",
];

function validZone(tz) {
  try { new Intl.DateTimeFormat("en-AU", { timeZone: tz }); return true; } catch (_) { return false; }
}

export function loadTimeZone() {
  try {
    const tz = window.localStorage.getItem(TZ_STORAGE_KEY);
    return tz && validZone(tz) ? tz : DEFAULT_TZ;
  } catch (_) { return DEFAULT_TZ; }
}

function parse(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const PLAIN_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

// "25 Oct 2026, 9:07am AEDT" (or "25 Oct 2026" with dateOnly). "—" for a missing or unreadable value.
// A plain calendar date ("2026-10-25") with dateOnly is shown as that date: it has no time, so no zone applies.
export function formatTime(iso, tz = DEFAULT_TZ, { dateOnly = false } = {}) {
  const plain = dateOnly && PLAIN_DATE.exec(String(iso || ""));
  if (plain && Number(plain[2]) >= 1 && Number(plain[2]) <= 12) {
    return `${Number(plain[3])} ${MONTHS[Number(plain[2]) - 1]} ${plain[1]}`;
  }
  const d = parse(iso);
  if (!d) return "—";
  const opts = { timeZone: tz, day: "numeric", month: "short", year: "numeric" };
  if (!dateOnly) Object.assign(opts, { hour: "numeric", minute: "2-digit", hour12: true, timeZoneName: "short" });
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-AU", opts).formatToParts(d).map((x) => [x.type, x.value]));
  const date = `${p.day} ${p.month} ${p.year}`;
  if (dateOnly) return date;
  return `${date}, ${p.hour}:${p.minute}${(p.dayPeriod || "").toLowerCase()} ${zoneName(d, tz, p.timeZoneName)}`;
}

// en-AU names Australian zones (AEDT, AWST) but writes others as offsets ("GMT-4"); en-US names the American ones
// (EDT) and en-GB the European ones (BST, CEST). Otherwise the offset stays.
const isOffset = (n) => /^GMT[+-]/.test(n || "");
function zoneName(d, tz, auName) {
  if (!isOffset(auName)) return auName;
  for (const locale of ["en-US", "en-GB"]) {
    const n = new Intl.DateTimeFormat(locale, { timeZone: tz, timeZoneName: "short" }).formatToParts(d)
      .find((x) => x.type === "timeZoneName")?.value;
    if (n && !isOffset(n)) return n;
  }
  return auName;
}

// "2026-10-24 22:07:00 UTC": the stored value, shown on hover.
export function utcLabel(iso) {
  const d = parse(iso);
  return d ? `${d.toISOString().slice(0, 19).replace("T", " ")} UTC` : "";
}

const TimeZoneContext = createContext({ tz: DEFAULT_TZ, setTz: () => {} });

export function TimeZoneProvider({ children, initial }) {
  const [tz, setTzState] = useState(() => initial || loadTimeZone());
  const setTz = useCallback((next) => {
    if (!validZone(next)) return;
    setTzState(next);
    try { window.localStorage.setItem(TZ_STORAGE_KEY, next); } catch (_) { /* private mode: this tab only */ }
  }, []);
  const value = useMemo(() => ({ tz, setTz }), [tz, setTz]);
  return <TimeZoneContext.Provider value={value}>{children}</TimeZoneContext.Provider>;
}

export const useTimeZone = () => useContext(TimeZoneContext);

// A timestamp in the admin's zone, with the UTC value on hover.
export function Time({ value, dateOnly = false, testid }) {
  const { tz } = useTimeZone();
  if (!parse(value)) return <span data-testid={testid}>—</span>;
  return (
    <time dateTime={value} title={dateOnly && PLAIN_DATE.test(value) ? undefined : utcLabel(value)} data-testid={testid}>
      {formatTime(value, tz, { dateOnly })}</time>
  );
}

export function TimeZoneSelect({ style }) {
  const { tz, setTz } = useTimeZone();
  const zones = TIMEZONES.includes(tz) ? TIMEZONES : [tz, ...TIMEZONES];
  return (
    <label style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span>Time zone</span>
      <select data-testid="admin-timezone" value={tz} onChange={(e) => setTz(e.target.value)} style={style}
              aria-label="Time zone for admin timestamps">
        {zones.map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
      </select>
    </label>
  );
}
